import React, { useState, useEffect } from 'react';
import { 
  User, 
  CreditCard, 
  Receipt, 
  LogOut,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle,
  Download,
  Plus,
  Wallet,
  ShoppingCart,
  Phone,
  Mail,
  Utensils,
  ChevronDown,
  Users,
  BarChart3,
  Filter,
  RefreshCw,
  UserPlus,
  QrCode,
  Copy,
  Timer,  Star,
  MessageSquare,
  MessageCircle,
  Loader2,
  Target
} from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ParentMessages from '../../components/messages/ParentMessages';
import { useParentsAuth } from '../../contexts/ParentsAuthContext';
import { useUnreadMessages } from '../../hooks/useUnreadMessages';
import { usePendingPix } from '../../hooks/usePendingPix';
import { dataHelpers, supabase } from '../../lib/supabase';
import { asaasHelpers } from '../../lib/asaasHelpers';
import ParentStudentForm from '../../components/forms/ParentStudentForm';
import QRCodeGenerator from '../../components/ui/QRCodeGeneratorNew';
import ReviewSection from '../../components/portal/ReviewSection';
import RechargeModal from '../../components/modals/RechargeModal';
import toast from 'react-hot-toast';
import { submitReview } from '../../lib/supabase';
import StudentBalanceCardFixed from '../../components/ui/StudentBalanceCardFixed';
import ParentsOnboarding from '../../components/onboarding/ParentsOnboarding';
import InteractiveTour, { useInteractiveTour } from '../../components/onboarding/InteractiveTour';
import { useOnboarding } from '../../hooks/useOnboarding';
import { useAdvancedOnboarding } from '../../hooks/useAdvancedOnboarding';

// Função auxiliar para formatar itens das vendas
const formatSaleItems = (items) => {
  if (!items) return [];
  
  try {
    // Se items já é um array, processar diretamente
    let itemsArray = Array.isArray(items) ? items : [];
    
    // Se items é uma string JSON, fazer parse
    if (typeof items === 'string') {
      try {
        itemsArray = JSON.parse(items);
      } catch (e) {
        return [items]; // Retorna array com a string original se não conseguir fazer parse
      }
    }
    
    // Se não é array após o processamento, retornar array com string
    if (!Array.isArray(itemsArray)) {
      return [String(items)];
    }
    
    // Formatar cada item com mais detalhes
    return itemsArray.map(item => {
      if (typeof item === 'object' && item.quantity) {
        const quantity = item.quantity;
        const unitPrice = item.unit_price || 0;
        const totalPrice = item.total_price || (quantity * unitPrice);
        
        // Se temos nome do produto, usar ele
        if (item.product_name) {
          return `${quantity}x ${item.product_name} (R$ ${unitPrice.toFixed(2)}) = R$ ${totalPrice.toFixed(2)}`;
        }
        
        // Senão, usar informações básicas
        return `${quantity}x R$ ${unitPrice.toFixed(2)} = R$ ${totalPrice.toFixed(2)}`;
      }
      return String(item);
    });
    
  } catch (error) {
    console.error('Erro ao formatar itens:', error);
    return [String(items)];
  }
};

// Função auxiliar para buscar nomes dos produtos e enriquecer itens
const enrichItemsWithProductNames = async (items, companyId) => {
  if (!items || !Array.isArray(items)) return items;
  
  try {
    // Coletar todos os product_ids únicos
    const productIds = items
      .filter(item => item.product_id && !item.product_name)
      .map(item => item.product_id);
    
    if (productIds.length === 0) return items;
    
    // Buscar produtos por IDs usando dataHelpers
    const products = await dataHelpers.getProducts(companyId);
    
    if (!products || products.length === 0) return items;
    
    // Criar mapa de id -> nome
    const productMap = {};
    products.forEach(product => {
      productMap[product.id] = product.name;
    });
    
    // Enriquecer itens com nomes dos produtos
    return items.map(item => ({
      ...item,
      product_name: item.product_name || productMap[item.product_id] || `Produto ${item.product_id?.substring(0, 8)}...`
    }));
    
  } catch (error) {
    console.error('Erro ao enriquecer itens com nomes dos produtos:', error);
    return items;
  }
};

// Função para buscar itens da venda na tabela sales baseado no sale_id
const fetchSaleItemsFromSalesTable = async (saleId, companyId) => {
  if (!saleId || !companyId) return null;
  
  try {
    console.log('🔍 Buscando itens da venda:', saleId);
    
    // Buscar venda específica pelo ID
    const sale = await dataHelpers.getSaleById(saleId, companyId);
    
    if (!sale || !sale.items) {
      console.warn('⚠️ Venda não encontrada ou sem itens:', saleId);
      return null;
    }
    
    // Parse dos itens se for string JSON
    let itemsArray = sale.items;
    if (typeof sale.items === 'string') {
      try {
        itemsArray = JSON.parse(sale.items);
      } catch (parseError) {
        console.error('Erro ao fazer parse dos itens da venda:', parseError);
        return null;
      }
    }
    
    if (!Array.isArray(itemsArray)) {
      console.warn('⚠️ Itens da venda não são um array válido:', itemsArray);
      return null;
    }
    
    // Enriquecer itens com nomes dos produtos
    const enrichedItems = await enrichItemsWithProductNames(itemsArray, companyId);
    
    console.log('✅ Itens da venda carregados e enriquecidos:', enrichedItems);
    return enrichedItems;
    
  } catch (error) {
    console.error('❌ Erro ao buscar itens da venda:', error);
    return null;
  }
};

const ParentsPortalOptimized = () => {  const { 
    parent, 
    students, 
    selectedStudent,    switchStudent,
    signOut, 
    refreshData,
    realtimeConnected,
    realtimeStatus,
    testRealtimeConnectivity,
    forceRealtimeReconnect,
    loading, // <-- Usar loading do contexto
    lastBalanceUpdate // <-- Timestamp para detectar mudanças de saldo
  } = useParentsAuth();
  
  const [activeTab, setActiveTab] = useState('dashboard');
  const [transactions, setTransactions] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [globalSummary, setGlobalSummary] = useState(null);
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [showStudentSelector, setShowStudentSelector] = useState(false);
  const [rechargeLoading, setRechargeLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('pix'); // 'pix' como padrão
  const [pixData, setPixData] = useState(null);
  const [pixLoading, setPixLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentTimer, setPaymentTimer] = useState(null);
  const [transactionFilters, setTransactionFilters] = useState({
    type: 'all',
    period: '30',
    startDate: null,
    endDate: null
  });
  const [globalFilters, setGlobalFilters] = useState({
    type: 'all',
    period: '30',
    student: 'all',
    startDate: null,
    endDate: null
  });
  const [showStudentForm, setShowStudentForm] = useState(false);
  const [studentFormLoading, setStudentFormLoading] = useState(false);  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [debugLogs, setDebugLogs] = useState([]);
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0 });  const [review, setReview] = useState({ rating: 0, comment: '' });
  const [reviewSent, setReviewSent] = useState(false);
  const company_id = selectedStudent?.company_id || (students[0]?.company_id);
    // Hook para mensagens não lidas
  const { unreadCount: unreadMessagesCount, refresh: refreshUnreadCount } = useUnreadMessages(parent?.id);
  // Hook para onboarding
  const { 
    shouldShowOnboarding, 
    isLoading: onboardingLoading,
    markAsCompleted: completeOnboarding,
    hideOnboarding,
    resetOnboarding 
  } = useOnboarding('parentsOnboardingCompleted');

  // Hook para tour interativo
  const {
    isActive: isTourActive,
    hasCompletedTour,
    startTour,
    completeTour,
    skipTour,
    resetTour
  } = useInteractiveTour('parentsPortalBasics');

  // Hook para onboarding avançado
  const {
    ONBOARDING_TYPES,
    shouldShowOnboarding: shouldShowAdvancedOnboarding,
    markPortalVisited
  } = useAdvancedOnboarding();
    // Expor função de refresh para o Realtime
  useEffect(() => {
    window.refreshUnreadMessages = refreshUnreadCount;
    return () => {
      delete window.refreshUnreadMessages;
    };
  }, [refreshUnreadCount]);
  // Marcar visita ao portal para onboarding avançado
  useEffect(() => {
    markPortalVisited();
  }, [markPortalVisited]);

  // Definir passos do tour interativo
  const tourSteps = [
    {
      selector: '[data-tour="student-selector"]',
      title: 'Seletor de Estudante',
      content: 'Aqui você pode alternar entre os seus filhos para ver informações específicas de cada um.',
      tip: 'Clique para trocar de estudante a qualquer momento'
    },
    {
      selector: '[data-tour="balance-card"]',
      title: 'Saldo do Estudante',
      content: 'Este é o saldo atual do seu filho. É atualizado em tempo real sempre que há uma compra ou recarga.',
      tip: 'O saldo aparece em verde quando suficiente e em vermelho quando baixo'
    },
    {
      selector: '[data-tour="recharge-button"]',
      title: 'Fazer Recarga',
      content: 'Clique aqui para adicionar créditos ao saldo do seu filho via PIX.',
      tip: 'As recargas são processadas automaticamente em poucos minutos'
    },
    {
      selector: '[data-tour="transaction-history"]',
      title: 'Histórico de Transações',
      content: 'Aqui você pode ver todas as compras e recargas do seu filho, com detalhes dos produtos comprados.',
      tip: 'Use os filtros para encontrar transações específicas'
    },
    {
      selector: '[data-tour="messages-tab"]',
      title: 'Mensagens da Escola',
      content: 'Receba comunicados importantes da escola sobre a alimentação e outros assuntos.',
      tip: 'Você será notificado quando houver novas mensagens'
    }
  ];
  
  // Hook para gerenciar PIX pendente
  const {
    pendingPix,
    savePendingPix,
    clearPendingPix,
    hasPendingPixForStudent,
    getPendingPixForStudent,
    isPendingPixValid
  } = usePendingPix(parent?.id, parent?.company_id);

  // Debug: Monitorar mudanças no contador de mensagens não lidas
  useEffect(() => {
    console.log('🔔 Unread messages count changed:', unreadMessagesCount);
    addDebugLog(`📬 Mensagens não lidas: ${unreadMessagesCount}`, 'info');
  }, [unreadMessagesCount]);
  // Função para adicionar log de debug
  const addDebugLog = (message, type = 'info') => {
    const log = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, // ID único combinando timestamp e string aleatória
      timestamp: new Date().toLocaleTimeString(),
      message,
      type
    };
    setDebugLogs(prev => [log, ...prev.slice(0, 19)]); // Manter apenas os últimos 20 logs
  };

  // Gerenciar menu de contexto
  const handleContextMenu = (e) => {
    if (process.env.NODE_ENV === 'development') {
      e.preventDefault();
      setContextMenu({
        show: true,
        x: e.pageX,
        y: e.pageY
      });
    }
  };

  const handleCloseContextMenu = () => {
    setContextMenu({ show: false, x: 0, y: 0 });
  };

  const handleToggleDebugPanel = () => {
    setShowDebugPanel(!showDebugPanel);
    handleCloseContextMenu();
  };

  // Fechar menu de contexto ao clicar em qualquer lugar
  useEffect(() => {
    const handleClick = () => {
      if (contextMenu.show) {
        handleCloseContextMenu();
      }
    };

    if (contextMenu.show) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu.show]);  // Monitor de mudanças nos estudantes para debug
  useEffect(() => {
    if (students.length > 0) {
      addDebugLog(`👥 ${students.length} estudantes carregados`, 'success');
      students.forEach(student => {
        addDebugLog(`💰 ${student.name}: R$ ${student.balance?.toFixed(2) || '0.00'}`, 'info');
      });
    }
  }, [students]);
  // Monitor específico para mudanças no saldo do estudante selecionado
  useEffect(() => {
    if (selectedStudent) {
      addDebugLog(`🎯 Saldo do estudante selecionado atualizado: ${selectedStudent.name} - R$ ${selectedStudent.balance?.toFixed(2) || '0.00'}`, 'info');
    }
  }, [selectedStudent?.balance, selectedStudent?.id]);

  // Monitor do status de conexão
  useEffect(() => {
    if (realtimeConnected) {
      addDebugLog('📡 Realtime conectado', 'success');
    } else {
      addDebugLog('⚠️ Apenas modo manual', 'warning');
    }
  }, [realtimeConnected]);
  
  // Função igual à StudentExtractPage.js para carregar extrato do aluno
  const loadStudentTransactions = async (studentId) => {
    try {
      setRechargeLoading(true);      
      const extractData = await dataHelpers.getStudentFinancialExtract(
        parent.company_id,
        studentId,
        {
          startDate: transactionFilters.startDate,
          endDate: transactionFilters.endDate,
          type: 'all' // Forçar 'all' para garantir que todas as transações sejam carregadas
        }
      );
      
      // Processar transações - os itens já vêm enriquecidos do backend
      const formattedTransactions = (extractData || []).map(transaction => {
          return {
            id: transaction.id,
            type: transaction.type,
            description: transaction.description,
            amount: parseFloat(transaction.amount) || 0,
            balance_after: parseFloat(transaction.balance_after) || 0,
            payment_method: transaction.payment_method,
            created_at: transaction.created_at,
            details: {
              ...transaction.details,
              items: transaction.items || [], // Itens já enriquecidos com nomes dos produtos
              sale_details: transaction.sale_details,
              operator: transaction.creator?.name || 'Sistema',
              category: transaction.category?.name
            }
          };
        });
      
      setTransactions(formattedTransactions);
      addDebugLog(`✅ ${formattedTransactions.length} transações carregadas`, 'success');
    } catch (error) {
      console.error('Error loading transactions:', error);
      addDebugLog(`❌ Erro ao carregar transações: ${error.message}`, 'error');
      toast.error('Erro ao carregar extrato');
      setTransactions([]);
    } finally {
      setRechargeLoading(false);
    }
  };
  // Carregar dados globais de todos os alunos
  const loadGlobalData = async () => {
    if (!parent?.email) return;
    
    try {
      setRechargeLoading(true);
      console.log('🌍 Carregando dados globais de todos os alunos...');
      
      // Usar a nova função que busca transações de todos os estudantes
      const allTransactions = await dataHelpers.getParentAllStudentsTransactions(parent.email, {
        startDate: globalFilters.startDate,
        endDate: globalFilters.endDate,
        limit: 100
      });
      
      // Processar transações para formato esperado pelo frontend
      const formattedTransactions = allTransactions.map(transaction => ({
        id: transaction.id,
        type: transaction.type,
        description: transaction.description,
        amount: parseFloat(transaction.amount) || 0,
        balance_after: parseFloat(transaction.balance_after) || 0,
        payment_method: transaction.payment_method,
        created_at: transaction.created_at,
        student_name: transaction.student_name,
        student_registration: transaction.student_registration,
        student_class: transaction.student_class,
        details: {
          ...transaction.details,
          items: transaction.items || [],
          sale_details: transaction.sale_details,
          operator: transaction.creator?.name || 'Sistema'
        }
      }));
      
      setAllTransactions(formattedTransactions);
      
      // Calcular resumo detalhado para relatórios
      const summary = {
        totalTransactions: formattedTransactions.length,
        totalCredits: formattedTransactions
          .filter(t => t.type === 'credit')
          .reduce((sum, t) => sum + Math.abs(t.amount), 0),
        totalDebits: formattedTransactions
          .filter(t => t.type === 'debit' || t.type === 'sale' || t.type === 'purchase')
          .reduce((sum, t) => sum + Math.abs(t.amount), 0),
        totalBalance: students.reduce((sum, student) => sum + student.balance, 0),
        studentsCount: [...new Set(formattedTransactions.map(t => t.student_name))].length,
        byStudent: {}
      };

      // Calcular dados por estudante
      const studentTransactions = {};
      formattedTransactions.forEach(transaction => {
        const studentName = transaction.student_name;
        const studentId = transaction.student_registration || studentName;
        
        if (!studentTransactions[studentId]) {
          studentTransactions[studentId] = {
            name: studentName,
            credits: 0,
            debits: 0,
            transactions: 0,
            balance: students.find(s => s.name === studentName)?.balance || 0
          };
        }
        
        studentTransactions[studentId].transactions++;
        
        if (transaction.type === 'credit') {
          studentTransactions[studentId].credits += Math.abs(transaction.amount);
        } else if (transaction.type === 'debit' || transaction.type === 'sale' || transaction.type === 'purchase') {
          studentTransactions[studentId].debits += Math.abs(transaction.amount);
        }
      });

      summary.byStudent = studentTransactions;
      setGlobalSummary(summary);
      
      console.log('✅ Dados globais carregados:', { 
        totalTransactions: formattedTransactions.length,
        summary 
      });
      addDebugLog(`✅ ${formattedTransactions.length} transações globais carregadas`, 'success');
    } catch (error) {
      console.error('❌ Erro ao carregar dados globais:', error);
      addDebugLog(`❌ Erro nos dados globais: ${error.message}`, 'error');
      toast.error('Erro ao carregar dados gerais');
    } finally {
      setRechargeLoading(false);
    }
  };

  // Carregar dados quando componente é montado
  useEffect(() => {
    if (parent?.email) {
      console.log('🚀 Carregando dados iniciais do portal...');
      addDebugLog('🚀 Inicializando portal dos pais', 'info');
      loadGlobalData();
      
      if (selectedStudent) {
        loadStudentTransactions(selectedStudent.id);
      }
    }
  }, [parent?.email, selectedStudent?.id]); // Dependências específicas

  useEffect(() => {
    if (selectedStudent && parent) {
      console.log('🔄 Carregando transações do estudante selecionado:', selectedStudent.name);
      addDebugLog(`📊 Carregando dados: ${selectedStudent.name}`, 'info');
      loadStudentTransactions(selectedStudent.id);
    }
  }, [selectedStudent, parent, transactionFilters]);

  useEffect(() => {
    if (parent?.email) {
      console.log('🌍 Recarregando dados globais devido a filtros...');
      addDebugLog('🌍 Atualizando dados globais', 'info');
      loadGlobalData();
    }
  }, [globalFilters]);

  // Listener para pagamento PIX recebido via realtime
  useEffect(() => {
    const handlePixPaymentReceived = (event) => {
      const { studentId, studentName, amount } = event.detail;
      
      console.log(`🎉 PIX recebido via realtime: ${studentName} (+R$ ${amount.toFixed(2)})`);
      
      // Se é o estudante selecionado e temos modal de PIX aberto
      if (selectedStudent && selectedStudent.id === studentId && showRechargeModal && paymentStatus === 'pending') {
        console.log('💰 Pagamento confirmado via realtime para estudante selecionado!');
        handlePaymentSuccess();
      }
    };

    // Adicionar listener
    window.addEventListener('pixPaymentReceived', handlePixPaymentReceived);

    // Cleanup
    return () => {
      window.removeEventListener('pixPaymentReceived', handlePixPaymentReceived);
    };
  }, [selectedStudent, showRechargeModal, paymentStatus]);

  const loadPortalData = async () => {
    try {
      setRechargeLoading(true);
      
      if (selectedStudent) {
        // Buscar extrato financeiro completo do aluno (igual StudentExtractPage)
        console.log('🔎 Portal dos Pais - Buscando extrato:', {
          company_id: parent.company_id,
          student_id: selectedStudent.id,
          parent: parent,
          student: selectedStudent
        });
        
        // Verificar se o company_id do parent bate com o company_id do student
        if (parent.company_id !== selectedStudent.company_id) {
          console.warn('⚠️ PROBLEMA: company_id diferente entre parent e student:', {
            parent_company_id: parent.company_id,
            student_company_id: selectedStudent.company_id
          });
        }
        try {
          const filters = {
            startDate: transactionFilters.startDate || null,
            endDate: transactionFilters.endDate || null,
            type: transactionFilters.type || 'all'
          };
          console.log('🔎 Filtros enviados para extrato:', filters);
          // Usar o company_id do student se disponível, senão do parent
          const companyId = selectedStudent.company_id || parent.company_id;
          console.log('🏢 Usando company_id:', companyId, 'fonte:', selectedStudent.company_id ? 'student' : 'parent');
            const extractData = await dataHelpers.getStudentFinancialExtract(
            companyId,
            selectedStudent.id,
            filters
          );
          console.log('✅ Extrato retornado:', extractData);
          
          // Processar transações e enriquecer itens com nomes dos produtos
          const formattedTransactions = await Promise.all(
            (extractData || []).map(async transaction => {
              let enrichedDetails = transaction.details;
              
              // Se tem itens, enriquecer com nomes dos produtos
              if (enrichedDetails?.items && companyId) {
                try {
                  const enrichedItems = await enrichItemsWithProductNames(
                    enrichedDetails.items,
                    companyId
                  );
                  enrichedDetails = {
                    ...enrichedDetails,
                    items: enrichedItems
                  };
                } catch (error) {
                  console.warn('Erro ao enriquecer itens:', error);
                }
              }
              
              return {
                id: transaction.id,
                type: transaction.type,
                description: transaction.description,
                amount: parseFloat(transaction.amount) || 0,
                balance_after: parseFloat(transaction.balance_after) || 0,
                payment_method: transaction.payment_method,
                created_at: transaction.processed_at, // StudentExtractPage usa processed_at
                details: {
                  ...enrichedDetails,
                  operator: transaction.creator?.name || 'Sistema',
                  category: transaction.category?.name
                }
              };
            })
          );
            console.log('📋 Transações formatadas:', formattedTransactions);
          setTransactions(formattedTransactions);
          // Remover toast informativo para não incomodar o usuário
          if (!extractData || extractData.length === 0) {
            console.log('ℹ️ Nenhuma transação encontrada para este aluno.');
          }
        } catch (transactionError) {
          console.warn('Error loading transactions:', transactionError);
          setTransactions([]);
        }

        // Buscar resumo financeiro
        try {
          const summary = await dataHelpers.getStudentFinancialSummary(selectedStudent.id);
          setFinancialSummary(summary);
          console.log('📊 Resumo financeiro carregado:', summary);
        } catch (summaryError) {
          console.warn('Error loading financial summary:', summaryError);
          setFinancialSummary(null);
        }
      }

    } catch (error) {
      console.error('Error loading portal data:', error);
      toast.error('Erro ao carregar dados do portal');
    } finally {
      setRechargeLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };  // Função para traduzir métodos de pagamento
  const translatePaymentMethod = (method) => {
    const translations = {
      'student_balance': 'Saldo do Aluno',
      'balance': 'Saldo',
      'cash': 'Dinheiro',
      'credit_card': 'Cartão de Crédito',
      'debit_card': 'Cartão de Débito',
      'pix': 'PIX',
      'bank_transfer': 'Transferência Bancária',
      'voucher': 'Voucher',
      'parents_portal': 'Portal dos Pais',
      'other': 'Outros'
    };
    return translations[method] || method;
  };

  // Função para traduzir tipos de pagamento
  const translatePaymentType = (type) => {
    const translations = {
      'prepaid': 'Pré-pago',
      'postpaid': 'Pós-pago',
      'credit': 'Crédito',
      'balance': 'Saldo'
    };
    return translations[type] || type;
  };
  const getTransactionIcon = (type) => {
    switch (type) {
      case 'credit':
        return (
          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <Plus className="w-4 h-4 text-green-600" />
          </div>
        );
      case 'purchase':
      case 'sale':
        return (
          <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
            <ShoppingCart className="w-4 h-4 text-red-600" />
          </div>
        );
      default:
        return (
          <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
            <Receipt className="w-4 h-4 text-gray-600" />
          </div>
        );
    }
  };
  const getTransactionColor = (transaction) => {
    return transaction.transaction_type === 'credit' ? 'text-green-600' : 'text-red-600';
  };
  
  const getTransactionPrefix = (transaction) => {
    return transaction.transaction_type === 'credit' ? '+' : '-';
  };  // Função para atualizar saldos manualmente
  const handleRefreshBalances = async () => {
    console.log('🔄 Atualizando saldos manualmente...');
    addDebugLog('🔄 Refresh manual iniciado', 'info');
    
    try {
      await refreshData();
      
      // Tentar reconectar o realtime se não estiver conectado
      if (!realtimeConnected && forceRealtimeReconnect) {
        console.log('📡 Tentando reconectar Realtime...');
        addDebugLog('📡 Reconectando Realtime...', 'info');
        forceRealtimeReconnect();
      }
      
      addDebugLog('✅ Refresh manual concluído', 'success');
      toast.success('Saldos atualizados!', { duration: 2000 });
    } catch (error) {
      console.error('Erro ao atualizar saldos:', error);
      addDebugLog(`❌ Erro no refresh: ${error.message}`, 'error');
      toast.error('Erro ao atualizar saldos');
    }
  };

  // Função de teste para simular mudança de saldo
  const handleTestBalanceUpdate = async () => {
    console.log('🧪 Teste: Simulando mudança de saldo...');
    addDebugLog('🧪 Teste de mudança de saldo iniciado', 'info');
    
    if (!selectedStudent) {
      toast.error('Selecione um estudante primeiro');
      addDebugLog('❌ Nenhum estudante selecionado', 'error');
      return;
    }

    try {
      // Simular uma pequena alteração no saldo para testar o sistema
      const newBalance = selectedStudent.balance + 0.01; // Adicionar 1 centavo
      
      setStudents(prevStudents => 
        prevStudents.map(student => 
          student.id === selectedStudent.id 
            ? { ...student, balance: newBalance, updated_at: new Date().toISOString() }
            : student
        )
      );
      
      setSelectedStudent(prev => ({ ...prev, balance: newBalance, updated_at: new Date().toISOString() }));
      
      addDebugLog(`💰 Teste: ${selectedStudent.name} R$ ${selectedStudent.balance.toFixed(2)} → R$ ${newBalance.toFixed(2)}`, 'success');
      toast.success('Teste: Saldo alterado artificialmente', { duration: 2000 });
    } catch (error) {
      console.error('Erro no teste:', error);
      addDebugLog(`❌ Erro no teste: ${error.message}`, 'error');
      toast.error('Erro no teste');
    }
  };

  // Função removida - lógica simplificada movida para createPixPayment

  // Função para criar cobrança PIX
  const createPixPayment = async (amount) => {
    console.log('🚀 INÍCIO - createPixPayment chamada');    console.log('📊 Dados iniciais:', {
      rechargeAmount: amount,
      selectedStudent: selectedStudent?.name,
      selectedStudent_financial_parent_id: selectedStudent?.financial_parent_id,
      parent: parent?.name,
      parent_id: parent?.id,
      company_id: parent?.company_id
    });    if (!amount || parseFloat(amount) <= 0) {
      console.log('❌ Valor inválido:', amount);
      toast.error('Digite um valor válido para recarga');
      return;
    }

    // Validar valor mínimo do PIX (R$ 5,00)
    if (parseFloat(amount) < 5.00) {
      console.log('❌ Valor abaixo do mínimo:', amount);
      toast.error('Valor mínimo para PIX é R$ 5,00');
      return;
    }if (!selectedStudent?.financial_parent_id) {
      console.log('❌ Responsável financeiro não definido para o aluno:', selectedStudent?.name);
      console.log('🔍 Dados completos do aluno selecionado:', selectedStudent);
      toast.error('Aluno não possui responsável financeiro definido. Por favor, entre em contato com a administração.');
      return;
    }

    console.log('✅ Responsável financeiro encontrado:', selectedStudent.financial_parent_id);

    try {
      console.log('🔄 Iniciando processo PIX...');
      setPixLoading(true);
      setPaymentStatus('creating');
      
      // Sempre usar PIX avulso para evitar problemas
      let customerId = null;
      console.log('💳 Usando PIX avulso (sem vincular cliente)');

      // Criar cobrança PIX
      console.log('🔄 Criando cobrança PIX... [v2024-01-20]');
      const companyIdToUse = parent.company_id; // Usar company_id do contexto atual
      console.log('📊 Dados para PIX:', {
        customerId,
        value: parseFloat(amount),
        student: selectedStudent.name,
        company_id: companyIdToUse
      });      console.log('📞 Chamando asaasHelpers.createPixPayment...');
      const pixPaymentData = {
        customer: null, // Será criado automaticamente com dados do responsável
        financial_parent_id: selectedStudent.financial_parent_id, // ID do responsável financeiro
        value: parseFloat(amount),
        description: `Recarga de crédito - Responsável: ${parent.name} - Aluno: ${selectedStudent.name}`,
        externalReference: `rech_${Date.now()}_${selectedStudent.id.slice(-8)}`,
        company_id: companyIdToUse
      };
      console.log('📋 Dados enviados para PIX (sempre avulso):', pixPaymentData);

      const result = await asaasHelpers.createPixPayment(pixPaymentData);

      console.log('📨 Resposta recebida do asaasHelpers:', result);

      if (!result.success) {
        console.log('❌ Erro retornado:', result.error);
        throw new Error(result.error || 'Erro desconhecido ao criar PIX');
      }      console.log('✅ PIX criado com sucesso:', result);
      console.log('🔍 Verificando estrutura do pixData:', {
        hasPixQrCode: !!result.pixQrCode,
        hasPayload: !!result.pixQrCode?.payload,
        payload: result.pixQrCode?.payload,
        payloadType: typeof result.pixQrCode?.payload,
        payloadLength: result.pixQrCode?.payload?.length      });
      
      console.log('🚀 INICIANDO CRIAÇÃO DO REGISTRO WEBHOOK_LOGS...');
      console.log('📍 Local: ParentsPortalOptimized.js linha ~784');
      
      // Criar registro na tabela webhook_logs com dados do estudante
      console.log('📝 Criando registro na tabela webhook_logs...');
      console.log('📊 Dados do estudante selecionado:', {
        id: selectedStudent.id,
        name: selectedStudent.name,
        balance: selectedStudent.balance
      });
      console.log('📊 Dados do responsável:', {
        id: parent.id,
        name: parent.name,
        company_id: parent.company_id
      });
      
      // ===== VERIFICAR SE JÁ EXISTE ANTES DE INSERIR =====
      try {
        // Primeiro verificar se já existe um registro para esta external_reference
        const { data: existingRecord, error: checkError } = await supabase
          .from('webhook_logs')
          .select('id, event_type')
          .eq('external_reference', pixPaymentData.externalReference)
          .eq('event_type', 'PAYMENT_CREATED')
          .single();
        
        if (checkError && checkError.code !== 'PGRST116') { // PGRST116 = nenhum resultado encontrado
          console.error('❌ Erro ao verificar webhook_logs existente:', checkError);
        }
        
        if (existingRecord) {
          console.log('⚠️ Registro já existe na webhook_logs, evitando duplicação:', existingRecord.id);
        } else {
          // Só inserir se não existir
          const webhookLogData = {
            event_type: 'PAYMENT_CREATED',
            payment_id: result.payment.id,
            external_reference: pixPaymentData.externalReference,
            payment_status: 'pending',
            payment_value: parseFloat(amount),
            student_id: selectedStudent.id, // Campo específico para o aluno
            raw_data: {
              student_id: selectedStudent.id,
              student_name: selectedStudent.name,
              student_balance: selectedStudent.balance,
              parent_id: parent.id,
              parent_name: parent.name,
              company_id: companyIdToUse,
              amount: parseFloat(amount),
              pix_data: result,
              created_via: 'PARENTS_PORTAL',
              recharge_for_student: true
            },
            received_at: new Date().toISOString(),
            processed: false
          };
          
          console.log('💾 Criando único registro na webhook_logs:', webhookLogData);
          
          const { error: webhookLogError } = await supabase
            .from('webhook_logs')
            .insert(webhookLogData);
          
          if (webhookLogError) {
            console.error('❌ Erro ao criar webhook_log:', webhookLogError);
          } else {
            console.log('✅ Registro criado na webhook_logs sem duplicação');
          }
        }
      } catch (webhookError) {
        console.error('❌ Erro ao processar webhook_log:', webhookError);
      }
      
      setPixData(result);
      setPaymentStatus('pending');
      
      // Salvar PIX pendente no localStorage
      savePendingPix(result, selectedStudent.id, amount);
      
      // Iniciar verificação de status
      startPaymentStatusCheck(result.payment.id);
      
      toast.success('PIX gerado com sucesso! Escaneie o QR Code para pagar.');

    } catch (error) {
      console.error('❌ Erro ao criar PIX:', error);
      
      // Mensagem de erro mais amigável
      let errorMessage = 'Erro ao gerar PIX';
      if (error.message.includes('Token Asaas')) {
        errorMessage = 'Configuração PIX não encontrada. Entre em contato com o suporte.';
      } else if (error.message.includes('400')) {
        errorMessage = 'Dados inválidos para criação do PIX. Verifique os dados e tente novamente.';
      } else {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      setPaymentStatus('error');
    } finally {
      setPixLoading(false);
    }
  };
  // Função para verificar status do pagamento
  const checkPaymentStatus = async (paymentId) => {
    try {
      const result = await asaasHelpers.checkPaymentStatus(paymentId, parent.company_id);
      
      if (result.success && result.isPaid) {
        console.log('💰 Pagamento confirmado via API:', paymentId);
        handlePaymentSuccess();
      }
      
      return result;
    } catch (error) {
      console.error('Erro ao verificar status:', error);
      return null;
    }
  };

  // Função para processar sucesso do pagamento
  const handlePaymentSuccess = () => {
    console.log('🎉 Processando sucesso do pagamento PIX');
    
    setPaymentStatus('paid');
    clearInterval(paymentTimer);
    
    // Limpar PIX pendente do localStorage
    clearPendingPix();
    
    // Mostrar mensagem de sucesso
    toast.success('🎉 Pagamento confirmado! Crédito adicionado com sucesso.', {
      duration: 4000,
      style: {
        background: '#10B981',
        color: '#fff',
        fontWeight: 'bold'
      }
    });
    
    // Fechar modal após 2 segundos
    setTimeout(() => {
      setShowRechargeModal(false);
      setPixData(null);
      setPaymentStatus('idle');
      
      console.log('✅ Modal de recarga fechado automaticamente');
      
      // Recarregar dados dos estudantes para mostrar saldo atualizado
      refreshData();
    }, 2000);
  };

  // Função para iniciar verificação periódica do status
  const startPaymentStatusCheck = (paymentId) => {
    const timer = setInterval(async () => {
      await checkPaymentStatus(paymentId);
    }, 5000); // Verificar a cada 5 segundos
    
    setPaymentTimer(timer);
    
    // Parar verificação após 10 minutos
    setTimeout(() => {
      clearInterval(timer);
      if (paymentStatus === 'pending') {
        setPaymentStatus('expired');
        toast.error('PIX expirado. Gere um novo PIX para continuar.');
      }
    }, 600000); // 10 minutos
  };
  // Função para cancelar PIX
  const cancelPixPayment = async () => {
    if (!pixData?.payment?.id) {
      toast.error('Nenhum PIX para cancelar');
      return;
    }

    try {
      console.log('🗑️ Iniciando cancelamento do PIX:', pixData.payment.id);
      setPixLoading(true);

      const result = await asaasHelpers.cancelPixPayment(pixData.payment.id, parent.company_id);      if (result.success) {
        console.log('✅ PIX cancelado com sucesso:', result);
        
        // Limpar dados do PIX
        setPixData(null);
        setPaymentStatus(null);
        
        // Limpar PIX pendente do localStorage
        clearPendingPix();
        
        // Parar verificação de status se estiver rodando
        if (paymentTimer) {
          clearInterval(paymentTimer);
          setPaymentTimer(null);
        }
        
        toast.success('PIX cancelado com sucesso!');
      } else {
        throw new Error(result.error || 'Erro ao cancelar PIX');
      }
    } catch (error) {
      console.error('❌ Erro ao cancelar PIX:', error);
      
      let errorMessage = 'Erro ao cancelar PIX';
      if (error.message.includes('já estava cancelado')) {
        errorMessage = 'PIX já estava cancelado';
        // Limpar dados mesmo assim
        setPixData(null);
        setPaymentStatus(null);
        if (paymentTimer) {
          clearInterval(paymentTimer);
          setPaymentTimer(null);
        }
      } else if (error.message.includes('já confirmado')) {
        errorMessage = 'Não é possível cancelar um PIX já pago';
      } else {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setPixLoading(false);
    }
  };

  // Função para processar recarga após confirmação PIX
  const processPixRecharge = async () => {
    try {
      const amount = parseFloat(rechargeAmount);
      const result = await dataHelpers.addStudentCredit(
        selectedStudent.id, 
        amount, 
        'Recarga via PIX - Portal dos Pais',
        'pix'
      );      if (result.success) {
        setShowRechargeModal(false);
        setRechargeAmount('');
        setPaymentMethod('manual');
        setPixData(null);
        setPaymentStatus(null);
        
        // Limpar PIX pendente do localStorage (processamento concluído)
        clearPendingPix();
        
        // Atualizar dados
        await refreshData();
        await loadPortalData();
        await loadGlobalData();
      }
    } catch (error) {
      console.error('Erro ao processar recarga:', error);
      toast.error(`Erro ao processar recarga: ${error.message}`);
    }
  };
  // Função para copiar código PIX
  const copyPixCode = () => {
    if (pixData?.pixQrCode?.payload) {
      navigator.clipboard.writeText(pixData.pixQrCode.payload);
      toast.success('Código PIX copiado!');
    }
  };

  // Função para restaurar PIX pendente
  const restorePendingPix = (pendingPixData) => {
    console.log('🔄 Restaurando PIX pendente:', pendingPixData);
    
    // Verificar se o PIX ainda é válido (menos de 10 minutos)
    if (!isPendingPixValid()) {
      console.log('⏰ PIX pendente expirado, removendo...');
      clearPendingPix();
      toast.error('PIX expirado. Gere um novo PIX para continuar.');
      return;
    }
    
    // Restaurar dados do PIX no estado
    setPixData({
      payment: pendingPixData.payment,
      pixQrCode: pendingPixData.pixQrCode
    });
    setPaymentStatus('pending');
    
    // Reiniciar verificação de status se necessário
    if (pendingPixData.payment?.id) {
      startPaymentStatusCheck(pendingPixData.payment.id);
    }
    
    toast.success('PIX pendente restaurado! Continue o pagamento.');
  };

  const handleStudentRegistration = async (studentData) => {
    try {
      setStudentFormLoading(true);
      console.log('🎓 Cadastrando novo aluno via Portal dos Pais:', studentData);
      
      // Criar dados do aluno combinando dados do formulário com dados do responsável
      const fullStudentData = {
        ...studentData,
        company_id: parent.company_id,
        active: true,
        // Garantir que os dados do responsável estão presentes
        parent_name: parent.name,
        parent_email: parent.email,
        parent_phone: parent.phone || studentData.parent_phone,
        parent_cpf: parent.document || studentData.parent_cpf
      };
      
      console.log('📝 Dados completos para cadastro:', fullStudentData);
      
      const result = await dataHelpers.createStudent(parent.company_id, fullStudentData);
      
      if (result && result.id) {
        console.log('✅ Aluno cadastrado com sucesso:', result);
        
        toast.success(
          `Aluno ${studentData.name} cadastrado com sucesso!`,
          {
            duration: 4000,
            icon: '🎓'
          }
        );
        
        setShowStudentForm(false);
        
        // Recarregar dados do contexto para incluir o novo aluno
        console.log('🔄 Atualizando lista de alunos...');
        await refreshData();
        
        // Recarregar dados globais
        await loadGlobalData();
        
        return result;
      } else {
        throw new Error('Resposta inválida do servidor');
      }
      
    } catch (error) {
      console.error('❌ Erro ao cadastrar aluno:', error);
      toast.error(`Erro ao cadastrar aluno: ${error.message}`);
      throw error;
    } finally {
      setStudentFormLoading(false);
    }
  };

  const filteredTransactions = transactions.filter(transaction => {
    if (transactionFilters.type !== 'all' && transaction.type !== transactionFilters.type) {
      return false;
    }
    
    const transactionDate = new Date(transaction.created_at);
    const now = new Date();
    const daysAgo = parseInt(transactionFilters.period);
    const filterDate = new Date(now.getTime() - (daysAgo * 24 * 60 * 60 * 1000));
    
    return transactionDate >= filterDate;
  });

  const filteredGlobalTransactions = allTransactions.filter(transaction => {
    // Filtro por tipo
    if (globalFilters.type !== 'all' && transaction.type !== globalFilters.type) {
      return false;
    }
    
    // Filtro por aluno
    if (globalFilters.student !== 'all' && transaction.student_id !== globalFilters.student) {
      return false;
    }
    
    // Filtro por período
    if (globalFilters.startDate && globalFilters.endDate) {
      const transactionDate = new Date(transaction.processed_at || transaction.created_at);
      const startDate = new Date(globalFilters.startDate);
      const endDate = new Date(globalFilters.endDate);
      return transactionDate >= startDate && transactionDate <= endDate;
    } else if (globalFilters.period !== 'all') {
      const transactionDate = new Date(transaction.processed_at || transaction.created_at);
      const now = new Date();
      const daysAgo = parseInt(globalFilters.period);
      const filterDate = new Date(now.getTime() - (daysAgo * 24 * 60 * 60 * 1000));
      return transactionDate >= filterDate;
    }
    
    return true;
  });  const tabs = [
    { id: 'dashboard', label: 'Visão Geral', icon: TrendingUp },
    { id: 'transactions', label: 'Extrato', icon: Receipt },
    { id: 'messages', label: 'Mensagens', icon: MessageSquare, badge: unreadMessagesCount },
    { id: 'reports', label: 'Relatórios', icon: BarChart3 },
    { id: 'register', label: 'Cadastrar Aluno', icon: UserPlus },
    { id: 'profile', label: 'Perfil', icon: User },
    { id: 'review', label: 'Avaliar', icon: Star }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gray-50" onContextMenu={handleContextMenu}>
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mr-3">
                  <Utensils className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">CantinaTech</h1>
                  <p className="text-xs text-gray-500">Portal dos Pais</p>
                </div>
              </div>
            </div>              <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm text-gray-600">Bem-vindo(a),</p>
                <p className="font-semibold text-gray-900">{parent?.name}</p>
              </div>                {/* Indicador de status das atualizações */}              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${
                  realtimeConnected ? 'bg-green-500 animate-pulse' : realtimeStatus === 'JOINING' ? 'bg-yellow-500 animate-pulse' : 'bg-gray-400'
                }`} />
                <span className="text-xs text-gray-500">
                  {realtimeConnected ? 'Tempo real' : realtimeStatus === 'JOINING' ? 'Conectando...' : 'Manual'}
                </span>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRefreshBalances}
                title="Atualizar saldos"
                disabled={loading}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>

              <Button variant="outline" size="sm" onClick={signOut}>
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Estudantes Cards Completos */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Meus Filhos</h2>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setActiveTab('register')}
              className="bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Cadastrar Novo Aluno
            </Button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {students.map((student) => (              <Card 
                key={student.id} 
                className={`p-6 transition-all hover:shadow-md ${
                  selectedStudent?.id === student.id 
                    ? 'ring-2 ring-blue-500 bg-blue-50' 
                    : 'hover:bg-gray-50'
                }`}
                data-tour={selectedStudent?.id === student.id ? 'balance-card' : undefined}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                      {student.photo_url ? (
                        <img 
                          src={student.photo_url} 
                          alt={student.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <User className="w-6 h-6 text-blue-600" />
                      )}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{student.name}</h3>
                      <p className="text-sm text-gray-500">{student.class} • {student.registration}</p>
                      <p className="text-sm text-gray-500">{student.email}</p>
                    </div>
                  </div>                  <div className="text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <div>
                        <p className="text-2xl font-bold text-green-600">{formatCurrency(student.balance)}</p>
                        <p className="text-sm text-gray-500">Saldo atual</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRefreshBalances();
                        }}
                        className="p-1 h-8 w-8 text-gray-400 hover:text-gray-600"
                        title="Atualizar saldo"
                        disabled={loading}
                      >
                        <RefreshCw className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Limite de crédito</p>
                      <p className="font-medium">{formatCurrency(student.credit_limit)}</p>
                    </div>                    <div>
                      <p className="text-gray-500">Tipo de pagamento</p>
                      <p className="font-medium">{translatePaymentType(student.payment_type)}</p>
                    </div>
                  </div>
                </div>                <div className="mt-4 flex space-x-2">                  <Button 
                    size="sm" 
                    onClick={() => switchStudent(student.id)}
                    variant={selectedStudent?.id === student.id ? "primary" : "outline"}
                    className={`flex-1 h-9 ${selectedStudent?.id === student.id ? 'ring-2 ring-blue-300 ring-offset-1' : ''}`}
                    data-tour={selectedStudent?.id !== student.id ? 'student-selector' : undefined}
                  >
                    {selectedStudent?.id === student.id ? 'Selecionado' : 'Selecionar'}
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => {
                      switchStudent(student.id);
                      setActiveTab('transactions');
                    }}
                    className="h-9 px-3"
                  >
                    Ver Extrato
                  </Button>                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => {
                      switchStudent(student.id);
                      setShowRechargeModal(true);
                      
                      // Verificar se existe PIX pendente para este aluno
                      const studentPendingPix = getPendingPixForStudent(student.id);
                      if (studentPendingPix && isPendingPixValid()) {
                        console.log('🔄 PIX pendente encontrado ao abrir modal:', studentPendingPix);
                        // O modal irá restaurar automaticamente via useEffect
                      }
                    }}
                    className="bg-green-50 text-green-700 hover:bg-green-100 border-green-200 h-9 px-3"
                    data-tour={selectedStudent?.id === student.id ? 'recharge-button' : undefined}
                  >
                    <Wallet className="w-4 h-4 mr-1" />
                    Recarregar
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>


        {/* Navigation Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    data-tour={tab.id === 'messages' ? 'messages-tab' : undefined}
                    className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center relative ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {tab.label}
                    {tab.badge && tab.badge > 0 && (
                      <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
                        {tab.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'dashboard' && (
          <div className="space-y-8">
            {/* Filtros Globais */}
            <Card className="p-6">
              <div className="flex items-center space-x-4 flex-wrap gap-4">
                <div className="flex items-center space-x-2">
                  <Filter className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Filtros Globais:</span>
                </div>
                
                <select
                  value={globalFilters.student}
                  onChange={(e) => setGlobalFilters(prev => ({ ...prev, student: e.target.value }))}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="all">Todos os alunos</option>
                  {students.map(student => (
                    <option key={student.id} value={student.id}>{student.name}</option>
                  ))}
                </select>

                <select
                  value={globalFilters.type}
                  onChange={(e) => setGlobalFilters(prev => ({ ...prev, type: e.target.value }))}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="all">Todos os tipos</option>
                  <option value="credit">Recargas</option>
                  <option value="purchase">Compras</option>
                  <option value="sale">Vendas</option>
                </select>

                <select
                  value={globalFilters.period}
                  onChange={(e) => setGlobalFilters(prev => ({ ...prev, period: e.target.value }))}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="7">Últimos 7 dias</option>
                  <option value="30">Últimos 30 dias</option>
                  <option value="90">Últimos 90 dias</option>
                  <option value="365">Último ano</option>
                </select>

                <div className="flex-1"></div>
                
                <Button variant="outline" size="sm" onClick={() => loadGlobalData()}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Atualizar
                </Button>
              </div>
            </Card>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-green-100">
                    <DollarSign className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Saldo Total</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {formatCurrency(globalSummary?.totalBalance || students.reduce((sum, student) => sum + student.balance, 0))}
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-blue-100">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Estudantes</p>
                    <p className="text-2xl font-semibold text-gray-900">{students.length}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-purple-100">
                    <Receipt className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total de Transações</p>
                    <p className="text-2xl font-semibold text-gray-900">{filteredGlobalTransactions.length}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-red-100">
                    <TrendingDown className="w-6 h-6 text-red-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total de Gastos</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {formatCurrency(globalSummary?.totalDebits || 0)}
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Recent Transactions */}
            <Card>
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Transações Recentes - Todos os Alunos</h3>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setActiveTab('transactions')}
                  >
                    Ver Todas
                  </Button>
                </div>
              </div>
              <div className="divide-y divide-gray-200">
                {filteredGlobalTransactions.slice(0, 10).map((transaction) => (
                  <div key={transaction.id} className="p-6 flex items-center justify-between">
                    <div className="flex items-center">
                      {getTransactionIcon(transaction.type)}
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">{transaction.description}</p>
                        <div className="flex items-center space-x-2 text-sm text-gray-500">
                          <span>{formatDateTime(transaction.processed_at || transaction.created_at)}</span>
                          <span>•</span>
                          <span className="font-medium text-blue-600">{transaction.student_name}</span>
                        </div>                        {/* Mostrar detalhes dos itens se existirem */}
                        {transaction.details && transaction.details.items && (
                          <div className="mt-1">                            <p className="text-xs text-gray-400 mb-1">Itens:</p>
                            {formatSaleItems(transaction.details.items).map((item, index) => (
                              <p key={`${transaction.id}-item-${index}`} className="text-xs text-gray-400 ml-2">
                                • {item}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-medium ${getTransactionColor(transaction)}`}>
                        {transaction.transaction_type === 'credit' ? '+' : '-'}{formatCurrency(transaction.amount)}
                      </p>
                      {transaction.balance_after !== undefined && transaction.balance_after !== 0 && (
                        <p className="text-xs text-gray-500">Saldo: {formatCurrency(transaction.balance_after)}</p>
                      )}
                    </div>
                  </div>
                ))}
                {filteredGlobalTransactions.length === 0 && (
                  <div className="p-6 text-center text-gray-500">
                    <Receipt className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>Nenhuma transação encontrada</p>
                    <p className="text-sm">As transações aparecerão aqui quando houver atividade</p>
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}


        {activeTab === 'transactions' && (
          <div className="space-y-6">
            {/* Filters */}
            <Card className="p-6">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <Filter className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Filtros:</span>
                </div>
                
                <select
                  value={transactionFilters.type}
                  onChange={(e) => setTransactionFilters(prev => ({ ...prev, type: e.target.value }))}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="all">Todos os tipos</option>
                  <option value="credit">Recargas</option>
                  <option value="purchase">Compras</option>
                  <option value="sale">Vendas</option>
                </select>

                <select
                  value={transactionFilters.period}
                  onChange={(e) => setTransactionFilters(prev => ({ ...prev, period: e.target.value }))}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="7">Últimos 7 dias</option>
                  <option value="30">Últimos 30 dias</option>
                  <option value="90">Últimos 90 dias</option>
                  <option value="365">Último ano</option>
                </select>

                <div className="flex-1"></div>
                
                <Button variant="outline" size="sm">
                  <Download className="w-4 h-4 mr-2" />
                  Exportar PDF
                </Button>
              </div>
            </Card>            {/* Transactions Table */}
            <Card data-tour="transaction-history">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Extrato de {selectedStudent?.name}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {filteredTransactions.length} transações encontradas
                  </p>
                </div>
              </div>
              {filteredTransactions.length > 0 ? (
                <div className="overflow-hidden">
                  <table className="w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Saldo</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredTransactions.map((transaction) => (
                        <tr key={transaction.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDateTime(transaction.created_at)}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            <div className="flex items-center">
                              {getTransactionIcon(transaction.type)}
                              <div className="ml-2">
                                <span>{transaction.description}</span>                                {/* Mostrar detalhes dos itens */}
                                {transaction.details && transaction.details.items && (
                                  <div className="mt-1">                                    <p className="text-xs text-gray-500 mb-1">Itens:</p>
                                    {formatSaleItems(transaction.details.items).map((item, index) => (
                                      <p key={`${transaction.id}-detail-${index}`} className="text-xs text-gray-500 ml-2">
                                        • {item}
                                      </p>
                                    ))}
                                  </div>
                                )}                                {/* Mostrar método de pagamento */}
                                {transaction.payment_method && transaction.payment_method !== 'balance' && (
                                  <div className="mt-1 flex items-center">
                                    <CreditCard className="w-3 h-3 text-blue-500 mr-1" />
                                    <p className="text-xs text-gray-500">
                                      Pagamento: {translatePaymentMethod(transaction.payment_method)}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              transaction.type === 'credit' 
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {transaction.type === 'credit' ? 'Recarga' : 
                               transaction.type === 'purchase' || transaction.type === 'sale' ? 'Compra' : 
                               transaction.type}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <span className={getTransactionColor(transaction)}>
                              {transaction.transaction_type === 'credit' ? '+' : '-'}{formatCurrency(transaction.amount)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {transaction.balance_after !== undefined && transaction.balance_after !== 0 
                              ? formatCurrency(transaction.balance_after)
                              : formatCurrency(selectedStudent?.balance || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-12 text-center text-gray-500">
                  <Receipt className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma transação encontrada</h3>
                  <p>Não há transações para exibir com os filtros selecionados.</p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-4"
                    onClick={() => setTransactionFilters({ type: 'all', period: '365' })}
                  >
                    Limpar Filtros
                  </Button>
                </div>
              )}
            </Card>
          </div>
        )}

        {activeTab === 'reports' && (
          <div className="space-y-6">
            {/* Filtros para Relatórios */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Filtros de Relatório</h3>
                <Button
                  onClick={loadGlobalData}
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={loading}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Atualizar Dados
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Período</label>
                  <select
                    value={globalFilters.period}
                    onChange={(e) => setGlobalFilters(prev => ({ ...prev, period: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="7">Últimos 7 dias</option>
                    <option value="30">Últimos 30 dias</option>
                    <option value="90">Últimos 90 dias</option>
                    <option value="all">Todo período</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Aluno</label>
                  <select
                    value={globalFilters.student}
                    onChange={(e) => setGlobalFilters(prev => ({ ...prev, student: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">Todos os alunos</option>
                    {students.map(student => (
                      <option key={student.id} value={student.id}>{student.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Transação</label>
                  <select
                    value={globalFilters.type}
                    onChange={(e) => setGlobalFilters(prev => ({ ...prev, type: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">Todos os tipos</option>
                    <option value="credit">Recargas</option>
                    <option value="debit">Débitos</option>
                    <option value="sale">Vendas</option>
                    <option value="purchase">Compras</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Exportar</label>
                  <Button
                    onClick={() => toast.success('Funcionalidade de exportação em desenvolvimento', {
                      duration: 3000,
                      icon: '📊',
                    })}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Exportar PDF
                  </Button>
                </div>
              </div>
            </Card>

            {globalSummary && (
              <>
                {/* Financial Overview - Global */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <Card className="p-6">
                    <div className="flex items-center">
                      <div className="p-3 rounded-full bg-green-100">
                        <TrendingUp className="w-6 h-6 text-green-600" />
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Total de Recargas</p>
                        <p className="text-2xl font-semibold text-green-600">
                          {formatCurrency(globalSummary.totalCredits)}
                        </p>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-6">
                    <div className="flex items-center">
                      <div className="p-3 rounded-full bg-red-100">
                        <ShoppingCart className="w-6 h-6 text-red-600" />
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Total de Gastos</p>
                        <p className="text-2xl font-semibold text-red-600">
                          {formatCurrency(globalSummary.totalDebits)}
                        </p>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-6">
                    <div className="flex items-center">
                      <div className="p-3 rounded-full bg-blue-100">
                        <Receipt className="w-6 h-6 text-blue-600" />
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Total de Transações</p>
                        <p className="text-2xl font-semibold text-blue-600">
                          {globalSummary.totalTransactions}
                        </p>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-6">
                    <div className="flex items-center">
                      <div className="p-3 rounded-full bg-purple-100">
                        <Wallet className="w-6 h-6 text-purple-600" />
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Saldo Total</p>
                        <p className="text-2xl font-semibold text-purple-600">
                          {formatCurrency(globalSummary.totalBalance)}
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Resumo por Aluno */}
                <Card>
                  <div className="p-6 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Resumo por Aluno</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aluno</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Saldo Atual</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recargas</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gastos</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transações</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Saldo Líquido</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {Object.entries(globalSummary.byStudent || {}).map(([studentId, data]) => (
                          <tr key={studentId} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                  <User className="w-4 h-4 text-blue-600" />
                                </div>
                                <span className="text-sm font-medium text-gray-900">{data.name}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <span className={`font-medium ${data.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formatCurrency(data.balance)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                              {formatCurrency(data.credits)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                              {formatCurrency(data.debits)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {data.transactions}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <span className={`font-medium ${
                                (data.credits - data.debits) >= 0 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {formatCurrency(data.credits - data.debits)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>

                {/* Gráfico de Gastos por Período */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <div className="p-6 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900">Análise de Tendências</h3>
                    </div>
                    <div className="p-6">
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                          <div>
                            <p className="text-sm text-green-700">Maior Recarga</p>
                            <p className="text-xl font-bold text-green-800">
                              {formatCurrency(Math.max(...Object.values(globalSummary.byStudent || {}).map(s => s.credits), 0))}
                            </p>
                          </div>
                          <TrendingUp className="w-8 h-8 text-green-600" />
                        </div>
                        
                        <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                          <div>
                            <p className="text-sm text-red-700">Maior Gasto</p>
                            <p className="text-xl font-bold text-red-800">
                              {formatCurrency(Math.max(...Object.values(globalSummary.byStudent || {}).map(s => s.debits), 0))}
                            </p>
                          </div>
                          <TrendingDown className="w-8 h-8 text-red-600" />                        </div>
                        <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                          <div>
                            <p className="text-sm text-blue-700">Aluno Mais Ativo</p>
                            <p className="text-lg font-bold text-blue-800">
                              {Object.values(globalSummary.byStudent || {})
                                .sort((a, b) => b.transactions - a.transactions)
                                .slice(0, 1)
                                .map(s => s.name)
                                .join('') || 'N/A'}
                            </p>
                          </div>
                          <TrendingUp className="w-8 h-8 text-blue-600" />
                        </div>
                      </div>
                    </div>
                  </Card>

                  <Card>
                    <div className="p-6 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900">Distribuição de Gastos</h3>
                    </div>
                    <div className="p-6">
                      <div className="space-y-3">
                        {Object.entries(globalSummary.byStudent || {})
                          .sort((a, b) => b[1].debits - a[1].debits)
                          .slice(0, 5)
                          .map(([studentId, data], index) => {
                            const maxDebit = Math.max(...Object.values(globalSummary.byStudent || {}).map(s => s.debits));
                            const percentage = maxDebit > 0 ? (data.debits / maxDebit) * 100 : 0;
                            
                            return (
                              <div key={studentId} className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium text-gray-700">{data.name}</span>
                                  <span className="text-sm text-gray-500">{formatCurrency(data.debits)}</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div 
                                    className={`h-2 rounded-full ${
                                      index === 0 ? 'bg-red-500' :
                                      index === 1 ? 'bg-orange-500' :
                                      index === 2 ? 'bg-yellow-500' :
                                      index === 3 ? 'bg-blue-500' : 'bg-gray-500'
                                    }`}
                                    style={{ width: `${percentage}%` }}
                                  ></div>                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Transações Recentes */}
                <Card>
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900">Transações Recentes</h3>
                      <Button
                        onClick={() => setActiveTab('transactions')}
                        variant="outline"
                        className="text-sm"
                      >
                        Ver Todas
                      </Button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aluno</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {allTransactions
                          .slice(0, 10)
                          .map((transaction) => (
                            <tr key={transaction.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {new Date(transaction.created_at).toLocaleDateString('pt-BR')}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                    <User className="w-4 h-4 text-blue-600" />
                                  </div>
                                  <span className="text-sm font-medium text-gray-900">
                                    {transaction.student_name}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  transaction.type === 'credit' 
                                    ? 'bg-green-100 text-green-800'
                                    : transaction.type === 'sale' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {transaction.type === 'credit' ? 'Recarga' : 
                                   transaction.type === 'sale' ? 'Venda' :
                                   transaction.type === 'purchase' ? 'Compra' : 'Débito'}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                                {transaction.description}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <span className={`font-medium ${
                                  transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'
                                }`}>
                                  {transaction.type === 'credit' ? '+' : '-'}{formatCurrency(Math.abs(transaction.amount))}
                                </span>
                              </td>
                            </tr>
                          ))}
                        {allTransactions.length === 0 && (
                          <tr>
                            <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                              Nenhuma transação encontrada para o período selecionado
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </>
            )}
            
            {!globalSummary && (
              <div className="text-center py-12">
                <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Carregando Relatórios</h3>
                <p className="text-gray-500 mb-4">
                  Aguarde enquanto carregamos os dados financeiros...
                </p>
                <Button onClick={loadGlobalData} className="bg-blue-600 hover:bg-blue-700">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Carregar Dados
                </Button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'register' && (
          <div className="space-y-6">
            <ParentStudentForm
              onSubmit={handleStudentRegistration}
              onCancel={() => setActiveTab('dashboard')}
              loading={studentFormLoading}
              parentData={parent}
            />
          </div>
        )}        {activeTab === 'messages' && (
          <div className="space-y-6">
            <ParentMessages 
              parentId={parent?.id}
              parentEmail={parent?.email}
              companyId={parent?.company_id}
              onMessageRead={refreshUnreadCount}
            />
          </div>
        )}        {activeTab === 'profile' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Parent Info */}
            <Card>
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Informações do Responsável</h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Nome Completo</label>
                  <p className="text-lg font-medium text-gray-900">{parent?.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">E-mail</label>
                  <p className="text-lg font-medium text-gray-900">{parent?.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Telefone</label>
                  <p className="text-lg font-medium text-gray-900">{parent?.phone || 'Não informado'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">CPF</label>
                  <p className="text-lg font-medium text-gray-900">{parent?.document || 'Não informado'}</p>
                </div>
                  {/* Botões de ajuda */}
                <div className="pt-4 border-t border-gray-200 space-y-3">
                  <Button
                    onClick={resetOnboarding}
                    variant="outline"
                    className="w-full flex items-center justify-center space-x-2"
                  >
                    <Users className="w-4 h-4" />
                    <span>Ver Tutorial Completo</span>
                  </Button>
                  
                  <Button
                    onClick={startTour}
                    variant="outline"
                    className="w-full flex items-center justify-center space-x-2"
                  >
                    <Target className="w-4 h-4" />
                    <span>Tour Rápido da Interface</span>
                  </Button>
                  
                  <p className="text-xs text-gray-500 text-center">
                    Reveja as funcionalidades do Portal dos Pais
                  </p>
                </div>
              </div>
            </Card>

            {/* Selected Student Info */}
            {selectedStudent && (
              <Card>
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Informações do Estudante Selecionado</h3>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Nome Completo</label>
                    <p className="text-lg font-medium text-gray-900">{selectedStudent.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Turma</label>
                    <p className="text-lg font-medium text-gray-900">{selectedStudent.class}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Matrícula</label>
                    <p className="text-lg font-medium text-gray-900">{selectedStudent.registration}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">E-mail</label>
                    <p className="text-lg font-medium text-gray-900">{selectedStudent.email || 'Não informado'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Data de Nascimento</label>
                    <p className="text-lg font-medium text-gray-900">
                      {selectedStudent.birth_date ? formatDate(selectedStudent.birth_date) : 'Não informado'}
                    </p>
                  </div>
                </div>
              </Card>
            )}
          </div>
        )}

        {activeTab === 'review' && (
          <ReviewSection 
            parent={parent}
            selectedStudent={selectedStudent}
            companyId={company_id}
            onReviewSubmitted={() => {
              setReviewSent(true);
              toast.success('Avaliação enviada com sucesso!');
            }}
          />
        )}
      </div>

      {/* Painel de Debug (apenas em desenvolvimento) */}
      {process.env.NODE_ENV === 'development' && showDebugPanel && (
        <div className="fixed bottom-4 right-4 w-96 max-h-96 bg-white border border-gray-200 rounded-lg shadow-lg p-4 overflow-hidden z-50">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900">🐛 Painel de Debug</h3>
            <button 
              onClick={() => setShowDebugPanel(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>          </div>
          <div className="space-y-2">
            <div className="text-xs">
              <div className="grid grid-cols-2 gap-2 mb-2 p-2 bg-gray-50 rounded">
                <div>Realtime: {realtimeConnected ? '✅' : '❌'}</div>
                <div>Status: {realtimeStatus}</div>
                <div>Estudantes: {students.length}</div>
                <div>Selecionado: {selectedStudent?.name || 'Nenhum'}</div>
              </div>
              <div className="grid grid-cols-2 gap-1 mb-2">
                {!realtimeConnected && forceRealtimeReconnect && (
                  <button
                    onClick={() => {
                      addDebugLog('📡 Tentando reconectar Realtime...', 'info');
                      forceRealtimeReconnect();
                    }}
                    className="bg-blue-100 text-blue-700 border border-blue-200 rounded px-2 py-1 text-xs hover:bg-blue-200"
                  >
                    📡 Reconectar
                  </button>
                )}
                
                {testRealtimeConnectivity && (
                  <button
                    onClick={() => {
                      addDebugLog('🧪 Testando conectividade...', 'info');
                      testRealtimeConnectivity();
                    }}
                    className="bg-purple-100 text-purple-700 border border-purple-200 rounded px-2 py-1 text-xs hover:bg-purple-200"
                  >
                    🧪 Testar
                  </button>
                )}
                
                {selectedStudent && (
                  <button
                    onClick={() => {
                      // Simula mudança de saldo (apenas para teste)
                      const randomAmount = (Math.random() * 100 - 50).toFixed(2);
                      addDebugLog(`🧪 Teste: Simulando mudança de saldo de R$ ${randomAmount}`, 'warning');
                      refreshData();
                    }}
                    className="bg-purple-100 text-purple-700 border border-purple-200 rounded px-2 py-1 text-xs hover:bg-purple-200"
                  >
                    🧪 Testar
                  </button>
                )}
              </div>
            </div>
            <div className="overflow-y-auto max-h-48 space-y-1">
              {debugLogs.map(log => (
                <div 
                  key={log.id} 
                  className={`text-xs p-1 rounded ${
                    log.type === 'success' ? 'bg-green-50 text-green-700' :
                    log.type === 'warning' ? 'bg-yellow-50 text-yellow-700' :
                    log.type === 'error' ? 'bg-red-50 text-red-700' :
                    'bg-gray-50 text-gray-700'
                  }`}
                >
                  <span className="text-gray-500">{log.timestamp}</span> {log.message}
                </div>
              ))}
            </div>          </div>
        </div>
      )}

      {/* Modal de Recarga */}
      <RechargeModal
        isOpen={showRechargeModal}
        onClose={() => {
          setShowRechargeModal(false);
          setPaymentMethod('pix');
          setPixData(null);
          setPaymentStatus(null);
          if (paymentTimer) clearInterval(paymentTimer);
        }}
        selectedStudent={selectedStudent}
        onCreatePixPayment={createPixPayment}
        onCancelPixPayment={cancelPixPayment}
        onCopyPixCode={copyPixCode}
        formatCurrency={formatCurrency}
        pixData={pixData}
        paymentStatus={paymentStatus}
        pixLoading={pixLoading}
        paymentTimer={paymentTimer}
        pendingPix={pendingPix}
        onRestorePendingPix={restorePendingPix}
      />      {/* Menu de Contexto (apenas em desenvolvimento) */}
      {process.env.NODE_ENV === 'development' && contextMenu.show && (
        <div 
          className="fixed bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-50"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <button
            onClick={handleToggleDebugPanel}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
          >
            🐛 Abrir Painel de Debug
          </button>
        </div>
      )}      {/* Onboarding do Portal dos Pais */}
      <ParentsOnboarding
        isOpen={shouldShowOnboarding && !onboardingLoading}
        onClose={hideOnboarding}
        onComplete={completeOnboarding}
        parentName={parent?.name}
      />

      {/* Tour Interativo */}
      <InteractiveTour
        isActive={isTourActive}
        onComplete={completeTour}
        onSkip={skipTour}
        steps={tourSteps}
      />
    </div>
  );
};

export default ParentsPortalOptimized;